import { FC, useContext, useEffect, useRef, useState } from 'react';
import CustomAvatar from '../../components/shared/CustomAvatar';
import appState from '../../state/AppStateContainer';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { LogoutIcon, SettingsIcon } from '../../components/svgs';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ServiceAuth } from '../../services/service-auth';
import { BidRequestDraftsContext } from '../../App';
import { darkenColor } from '../../util';
import { useTranslation } from 'react-i18next';
import styles from './UserProfileMenu.module.scss';

type Params = {
  areaSlug: string;
  project: string;
  version: string;
};

type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type Props = {
  history: RouteTypes['history'];
}

const UserProfileMenu : FC<Props> = ({ history }) => {
  const [t] = useTranslation('profile');
  const [, setBidRequestDrafts] = useContext(BidRequestDraftsContext);
  const [showOptions, setShowOptions] = useState(false);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickEvent = (e : MouseEvent) => {
      const wrapperElement = menu.current;
      if (wrapperElement && !wrapperElement.contains(e.target as Node) && showOptions) {
        setShowOptions(false);
      }
    }
    if (showOptions) {
      document.addEventListener('click', handleClickEvent);
    } else {
      document.removeEventListener('click', handleClickEvent);
    }
    return () => document.removeEventListener('click', handleClickEvent);
  }, [showOptions]);

  const orgSlug = appState.getCurrentOrgSlug();
  const userName = () => appState.state.token?.name;
  const userEmail = () => appState.state.token?.email;
  const onSettings = () => {
    setShowOptions(false);
    history.push(`/${orgSlug}/profile`);
  };
  const onToggleSwitcher = async () => {
    setShowOptions(!showOptions);
  };
  const logoutClickCallback = async () => {
    setShowOptions(false);
    setBidRequestDrafts({});
    await ServiceAuth.logout();
    const root_theme = document.querySelector(':root') as HTMLElement;

    if (root_theme?.style) {
      root_theme.style.setProperty('--primary-button-background-color', '#ff5e15');
      root_theme.style.setProperty('--primary-button-background-color-hover', '#EA580C');
      root_theme.style.setProperty('--primary-button-text', 'white');
      root_theme.style.setProperty('--secondary-button-background-color', 'white');
      root_theme.style.setProperty('--secondary-button-background-color-hover', darkenColor('var(--secondary-button-background-color)', '97%'));
      root_theme.style.setProperty('--secondary-button-text', 'var(--gray-700)');
      root_theme.style.setProperty('--menu-text-color', 'white');
      root_theme.style.setProperty('--menu-text-color-not-focused', darkenColor('var(--menu-text-color)', '90%'));
      root_theme.style.setProperty('--background-image', '');
      root_theme.style.setProperty('--menu-background-color', 'var(--gray-800)');
      root_theme.style.setProperty('--primaryFont', 'Inter');
      appState.setLogoUrl(undefined);
    }
    history.push('/');
  };

  return (
    <div ref={menu} className={styles.container}>
      <div className={styles.toggle} onClick={onToggleSwitcher}>
        <CustomAvatar
          alt={userName()}
          src={appState.getAvatarUrl()}
          size={'38px'}
        />
        <ExpandMoreIcon fontSize="small" style={{display: showOptions ? 'none' : 'inline'}} />
        <ExpandLessIcon fontSize="small" style={{display: showOptions ? 'inline' : 'none'}} />
      </div>
      <ul className={`${styles.dropdown} ${showOptions ? styles['dropdown--show'] : ''}`}>
        <li className={styles.user}>
          <CustomAvatar
            alt={userName()}
            src={appState.getAvatarUrl()}
            size={'70px'}
          />
          <div>
            <div className={styles.name}>{userName()}</div>
            <div className={styles.email}>{userEmail()}</div>
          </div>
        </li>
        <li onClick={onSettings}>
          <SettingsIcon />
          {t('userMenu.profile', 'Profil och konto')}
        </li>
        <li onClick={logoutClickCallback}>
          <LogoutIcon />
          {t('userMenu.logout', 'Logga ut')}
        </li>
      </ul>
    </div>
  );
}

export default withRouter(UserProfileMenu);
